import React, { ReactNode } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { config } from '../index';
import { ContentWrapper } from './ContentWrapper';
import { AppSearch } from './AppSearch';

const CLOUD_FRONT = 'https://d3lu8cieuv8zqi.cloudfront.net/';
const URI = 'rentify/a206d64c-2134-4e7f-bb32-ff3317a3b117';
const HOME_BANNER_IMG = CLOUD_FRONT + URI;

export interface HomeBannerProps {
  /* Banner background color */
  backgroundColor?: string;
  /* Banner max width */
  maxWidth?: string;
  /* Banner min height */
  minHeight?: string;
  /* Banner child components */
  children?: ReactNode;
  /* Banner onClick callBack*/
  onClick?: (event?: React.MouseEvent) => void;
  /* Search input onChange handler */
  onChange?: (param?: any) => void;
  /* onClear callBack */
  onClear?: (param?: any) => void;
  /* onSelect category callBack */
  onSelected?: (param?: any) => void;
}

const TitleWrapper = styled.div<HomeBannerProps>`
  flex-direction: column;
  flex-wrap: wrap;
  font-size: 108px;
  line-height: 108px;
  font-weight: 700;
  font-family: 'DMSans';
  padding-right: 40%;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointXXXLarge}) {
    font-size: 72px;
    line-height: 72px;
  }

  @media (max-width: ${config.breakPointXLarge}) {
    font-size: 48px;
    line-height: 48px;
  }

  @media (max-width: ${config.breakPointLarge}) {
    font-size: 44px;
    line-height: 44px;
    padding-right: 20px;
  }
`;

const HomeBannerWrapper = styled.div<HomeBannerProps>`
  max-width: ${(props) => props.maxWidth};
  margin: 0 auto;
  background-image: url(${HOME_BANNER_IMG});
  background-repeat: no-repeat;
  background-position: 100% 0%;
  background-size: 1100px 1100px;

  @media (max-width: ${config.breakPointXLarge}) {
    background-size: 800px 800px;
  }

  @media (max-width: ${config.breakPointLarge}) {
    background-size: 600px 600px;
  }

  @media (max-width: ${config.breakPointMedium}) {
    background-size: 500px 500px;
    background-position: 50% 100px;
  }
`;

const BannerContentWrapper = styled.div<HomeBannerProps>`
  margin: 185px 0 0 30px;
  color: ${config.secondary};
  padding: 0 100px;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointXXXLarge}) {
    padding: 0 50px;
  }
  @media (max-width: ${config.breakPointXLarge}) {
    padding: 0;
    margin: 80px 0 0 20px;
  }
`;

const BodyWrapper = styled.div<HomeBannerProps>`
  font-size: 26px;
  color: ${config.grey};
  margin: 45px 0;
  padding-right: 60%;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointXLarge}) {
    font-size: 20px;
    max-width: 500px;
  }

  @media (max-width: ${config.breakPointLarge}) {
    padding-right: 20px;
  }
`;

const TopLineWrapper = styled.div<HomeBannerProps>`
  display: flex;
  flex-direction: row;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointMedium}) {
    flex-direction: column;
  }
`;

const AppSearchWrapper = styled.div<HomeBannerProps>`
  margin: auto 0 210px 0;
  padding-right: 40%;

  @media (max-width: ${config.breakPointXXXLarge}) {
    padding-right: 20%;
  }
  @media (max-width: ${config.breakPointLarge}) {
    margin: 0 auto 50px auto;
    padding-right: 20px;
  }
`;

const ContainerWrapper = styled.div<HomeBannerProps>`
  margin: 0 100px;
  transition: ${config.transitionShort};

  @media (max-width: ${config.breakPointLarge}) {
    margin: 0;
  }
`;

const AnimatedString = styled.div<HomeBannerProps>`
  position: relative;
  color: ${config.primary};
  width: 0; // this is the trick to prevent the text from wrapping

  &:after {
    padding: 0 38px; // accommodate space between words
    content: 'anything'; // content to be animated
    // 📌 animation config
    transform-origin: 50% 50%;
    transform-style: preserve-3d;
    transform: rotateX(0deg);
    animation: changeText 12s linear infinite; // animation name, duration, timing function, iteration count
    animation-delay: 1s;
    animation-fill-mode: forwards;
    animation-direction: alternate;
    animation-iteration-count: infinite;
    animation-play-state: running;
    animation-timing-function: ease-in-out;
    white-space: nowrap; // prevent line break
  }

  @media (max-width: ${config.breakPointXXXLarge}) {
    &:after {
      padding: 0 1.5vw; // accommodate space between words
    }
  }
  @media (max-width: ${config.breakPointMedium}) {
    &:after {
      padding: 0; // accommodate space between words
    }
  }

  // animate text by rotating X axis around the center of the element
  // electronics | cars | sports | party & wedding | fashion | home & garden | health & beauty | baby & kids | pets | other
  @keyframes changeText {
    0% {
      transform: rotateX(0deg);
    }
    10% {
      transform: rotateX(90deg);
      color: ${config.secondary};
      // apply text gradient from primary color to secondary color
      background: linear-gradient(
        90deg,
        ${config.primary},
        ${config.secondary}
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    20% {
      transform: rotateX(0deg);
      color: ${config.primary};
      content: 'electronics';
    }
    30% {
      transform: rotateX(90deg);
      color: ${config.secondary};
      // apply text gradient from primary color to secondary color
      background: linear-gradient(
        90deg,
        ${config.primary},
        ${config.secondary}
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    40% {
      transform: rotateX(0deg);
      color: ${config.primary};
      content: 'sports';
    }
    50% {
      transform: rotateX(90deg);
      color: ${config.secondary};
      // apply text gradient from primary color to secondary color
      background: linear-gradient(
        90deg,
        ${config.primary},
        ${config.secondary}
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    60% {
      transform: rotateX(0deg);
      color: ${config.primary};
      content: 'party & wedding';
    }
    70% {
      transform: rotateX(90deg);
      color: ${config.secondary};
      // apply text gradient from primary color to secondary color
      background: linear-gradient(
        90deg,
        ${config.primary},
        ${config.secondary}
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    80% {
      transform: rotateX(0deg);
      color: ${config.primary};
      content: 'home & garden';
    }
    90% {
      transform: rotateX(90deg);
      color: ${config.secondary};
      // apply text gradient from primary color to secondary color
      background: linear-gradient(
        90deg,
        ${config.primary},
        ${config.secondary}
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    100% {
      transform: rotateX(0deg);
      color: ${config.primary};
      content: 'tools';
    }
  }
`;

export const HomeBanner: React.FC<HomeBannerProps> = ({
  backgroundColor = config.silver,
  minHeight = '948px',
  maxWidth = '1920px',
  onClear,
  onSelected,
  ...props // additional props
}) => {
  return (
    <HomeBannerWrapper maxWidth={maxWidth}>
      <ContainerWrapper>
        <ContentWrapper
          backgroundColor={`rgba(0, 0, 0, 0.1)`}
          maxWidth={maxWidth}
          {...props}
        >
          <BannerContentWrapper>
            <TitleWrapper>
              <TopLineWrapper>
                <span>Rent</span>
                <AnimatedString />
              </TopLineWrapper>
              <span>from people in your area</span>
            </TitleWrapper>

            <BodyWrapper>
              Borrow almost anything from people nearby for jobs at home, fun
              experiences or work projects.
            </BodyWrapper>

            <AppSearchWrapper>
              <AppSearch onClear={onClear} onSelected={onSelected} />
            </AppSearchWrapper>
          </BannerContentWrapper>
        </ContentWrapper>
      </ContainerWrapper>
    </HomeBannerWrapper>
  );
};
